import { useEffect, useState } from "react";
import { makeRequestAuth } from "../../_config/api";
import { showErrorToast, showSuccessToast } from "../../utils/toastUtils";
import {
  FiX,
  FiSave,
  FiEdit2,
  FiEye,
  FiSearch,
  FiPlus,
  FiCheckCircle,
  FiFilter,
} from "react-icons/fi";

const themeBlue = "#3B82F6"; // Modern blue shade

export default function ExamList() {
  const [exams, setExams] = useState([]);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [year, setYear] = useState("");
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [editExam, setEditExam] = useState(null);

  const getExams = async (pageNo = 1) => {
    try {
      setLoading(true);
      const payload = {
        keyWord: search || undefined,
        status: status === "all" ? "" : status,
        testYear: year || undefined,
        pageNo,
        size: 20,
      };
      const res = await makeRequestAuth("post", "/admin/getExamList", payload);
      if (res?.error) {
        showErrorToast(res.message || "Failed to fetch exams");
        setExams([]);
        return;
      }
      setExams(res?.data?.list || []);
      setTotalPages(
        res?.data?.pagination?.totalPages ||
          Math.ceil((res?.data?.total || 0) / 20) ||
          1,
      );
    } catch {
      showErrorToast("Failed to load exams");
      setExams([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setPage(1);
    getExams(1);
  }, [search, status, year]);

  const changePage = (p) => {
    setPage(p);
    getExams(p);
  };

  return (
    <div className="p-4">
      <div className="max-w-7xl mx-auto">
        {/* HEADER SECTION */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-8">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Exam Management
            </h1>
            <p className="text-gray-500 text-sm">
              Create and manage your portal's examinations
            </p>
          </div>
          <a
            href="/test/create"
            className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-5 py-2.5 rounded-xl transition-all shadow-lg shadow-blue-200 font-semibold"
          >
            <FiPlus /> Create New Exam
          </a>
        </div>

        {/* FILTERS BAR */}
        <div className="bg-white p-4 rounded-2xl shadow-sm border border-gray-100 flex flex-wrap gap-4 mb-6 items-center">
          <div className="relative flex-1 min-w-[240px]">
            <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              placeholder="Search by exam name..."
              className="w-full pl-10 pr-4 py-2 bg-gray-50 border-none rounded-xl focus:ring-2 focus:ring-blue-500 transition-all outline-none"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <div className="flex items-center gap-2">
            <FiFilter className="text-gray-400" />
            <select
              className="bg-gray-50 border-none rounded-xl px-4 py-2 focus:ring-2 focus:ring-blue-500 outline-none cursor-pointer"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="draft">Draft</option>
            </select>

            <select
              className="bg-gray-50 border-none rounded-xl px-4 py-2 focus:ring-2 focus:ring-blue-500 outline-none cursor-pointer"
              value={year}
              onChange={(e) => setYear(e.target.value)}
            >
              <option value="">All Years</option>
              {Array.from({ length: 5 }).map((_, i) => {
                const y = new Date().getFullYear() - i;
                return (
                  <option key={y} value={String(y)}>
                    {y}
                  </option>
                );
              })}
            </select>
          </div>
        </div>

        {/* LIST TABLE */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          {loading ? (
            <div className="p-20 text-center text-gray-400">
              Loading exams...
            </div>
          ) : exams.length === 0 ? (
            <div className="p-20 text-center text-gray-400 italic">
              No exams match your criteria.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-gray-50/50 border-b border-gray-100">
                    <th className="px-6 py-4 text-xs font-bold uppercase text-gray-500">
                      Exam Details
                    </th>
                    <th className="px-6 py-4 text-xs font-bold uppercase text-gray-500">
                      Year
                    </th>
                    <th className="px-6 py-4 text-xs font-bold uppercase text-gray-500">
                      Stats
                    </th>
                    <th className="px-6 py-4 text-xs font-bold uppercase text-gray-500">
                      Status
                    </th>
                    <th className="px-6 py-4 text-xs font-bold uppercase text-gray-500 text-right">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {exams.map((exam) => (
                    <tr
                      key={exam._id}
                      className="hover:bg-blue-50/30 transition-colors group"
                    >
                      <td className="px-6 py-4">
                        <div className="font-bold text-gray-900 group-hover:text-blue-600 transition-colors">
                          {exam.testName}
                        </div>
                        <div className="flex gap-2 mt-1.5">
                          {exam.difficulty && (
                            <span className="text-[10px] px-2 py-0.5 bg-blue-50 text-blue-600 rounded-md font-bold uppercase tracking-wider">
                              {exam.difficulty}
                            </span>
                          )}
                          {exam.isPopular && (
                            <span className="text-[10px] px-2 py-0.5 bg-orange-50 text-orange-600 rounded-md font-bold uppercase tracking-wider">
                              Popular
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600 font-medium">
                        {exam.testYear || exam.year}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        <span className="block font-semibold text-gray-700">
                          {exam.totalQuestions} Qs
                        </span>
                        <span className="text-xs">
                          {exam.durationMinutes} Mins
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold border ${
                            exam.status === "active"
                              ? "bg-green-50 text-green-700 border-green-100"
                              : exam.status === "inactive"
                                ? "bg-red-50 text-red-700 border-red-100"
                                : "bg-gray-50 text-gray-600 border-gray-200"
                          }`}
                        >
                          <span
                            className={`w-1.5 h-1.5 rounded-full mr-1.5 ${
                              exam.status === "active"
                                ? "bg-green-500"
                                : "bg-gray-400"
                            }`}
                          />
                          {exam.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex justify-end gap-2">
                          {/* CHECK ATTEMPT (Only if active) */}
                          {/* {exam.status === "active" && (
                            <a
                              href={`/test/attempts/${exam._id}`}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-indigo-50 text-indigo-600 rounded-lg text-xs font-bold hover:bg-indigo-100 transition-all border border-indigo-100"
                              title="User Attempts"
                            >
                              <FiCheckCircle size={14} />
                              Attempts
                            </a>
                          )} */}
                          <button
                            onClick={() => setEditExam(exam)}
                            className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all"
                            title="Edit Exam"
                          >
                            <FiEdit2 size={18} />
                          </button>
                          <a
                            href={`/test/${exam._id}`}
                            className="p-2 text-gray-400 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-all"
                            title="View Details"
                          >
                            <FiEye size={18} />
                          </a>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* PAGINATION */}
          {totalPages > 1 && (
            <div className="p-4 border-t border-gray-50 flex justify-center gap-1.5 bg-gray-50/30">
              {Array.from({ length: totalPages }).map((_, i) => (
                <button
                  key={i}
                  onClick={() => changePage(i + 1)}
                  className={`min-w-[40px] h-10 rounded-xl font-bold transition-all ${
                    page === i + 1
                      ? "bg-blue-600 text-white shadow-md shadow-blue-200"
                      : "bg-white text-gray-500 hover:bg-gray-100 border border-gray-200"
                  }`}
                >
                  {i + 1}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {editExam && (
        <ExamEditModal
          exam={editExam}
          onClose={() => setEditExam(null)}
          onSuccess={() => getExams(page)}
        />
      )}
    </div>
  );
}

function ExamEditModal({ exam, onClose, onSuccess }) {
  const [form, setForm] = useState({
    testName: exam.testName,
    durationMinutes: exam.durationMinutes,
    difficulty: exam.difficulty,
    language: exam.language,
    isPopular: exam.isPopular,
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSave = async () => {
    try {
      const res = await makeRequestAuth("post", "/admin/updateExam", {
        examId: exam._id,
        ...form,
      });
      if (res?.error) return showErrorToast(res.message);
      showSuccessToast("Exam updated successfully");
      onSuccess();
      onClose();
    } catch (err) {
      showErrorToast("Update failed");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative bg-white w-full max-w-md rounded-3xl shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-200">
        <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
          <h2 className="text-lg font-bold text-gray-800">Edit Details</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-200 rounded-full text-gray-500 transition-colors"
          >
            <FiX size={20} />
          </button>
        </div>

        <div className="p-6 space-y-5">
          <div>
            <label className="text-xs font-bold text-gray-400 uppercase mb-1.5 block ml-1">
              Exam Title
            </label>
            <input
              name="testName"
              value={form.testName}
              onChange={handleChange}
              className="w-full bg-gray-50 border-none rounded-xl px-4 py-3 focus:ring-2 focus:ring-blue-500 outline-none"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-bold text-gray-400 uppercase mb-1.5 block ml-1">
                Duration (Min)
              </label>
              <input
                type="number"
                name="durationMinutes"
                value={form.durationMinutes}
                onChange={handleChange}
                className="w-full bg-gray-50 border-none rounded-xl px-4 py-3 focus:ring-2 focus:ring-blue-500 outline-none"
              />
            </div>
            <div>
              <label className="text-xs font-bold text-gray-400 uppercase mb-1.5 block ml-1">
                Difficulty
              </label>
              <select
                name="difficulty"
                value={form.difficulty}
                onChange={handleChange}
                className="w-full bg-gray-50 border-none rounded-xl px-4 py-3 focus:ring-2 focus:ring-blue-500 outline-none"
              >
                <option value="easy">Easy</option>
                <option value="medium">Medium</option>
                <option value="hard">Hard</option>
              </select>
            </div>
          </div>

          <label className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl cursor-pointer hover:bg-blue-50 transition-colors group">
            <input
              type="checkbox"
              name="isPopular"
              checked={form.isPopular}
              onChange={handleChange}
              className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="text-sm font-semibold text-gray-700 group-hover:text-blue-600">
              Highlight as Popular Exam
            </span>
          </label>
        </div>

        <div className="p-6 pt-0">
          <button
            onClick={handleSave}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3.5 rounded-2xl font-bold shadow-lg shadow-blue-200 transition-all flex items-center justify-center gap-2"
          >
            <FiSave /> Save Changes
          </button>
        </div>
      </div>
    </div>
  );
}
